/**
 * ListElement — F12.H T12.H.5.
 *
 * Lista wypunktowana (`<ul>`) lub numerowana (`<ol>`) z editowalnymi
 * itemami. Element dekoracyjny — nie jest polem formularza.
 *
 * Settings shape:
 *   { items: string[], ordered?: boolean }
 */

import type { LayoutElementDefinition } from '@/components/fields/LayoutElementRegistry';

export const ListElement: LayoutElementDefinition = {
  type: 'list',
  category: 'tresc',
  isContainer: false,
  label: 'Lista',
  icon: 'List',
  defaultSettings: () => ({
    items: ['Pierwszy element', 'Drugi element', 'Trzeci element'],
    ordered: false,
  }),
  propertiesEditor: ({ settings, onChange }) => {
    const items = Array.isArray(settings.items)
      ? (settings.items as unknown[]).map((s) => String(s ?? ''))
      : [];
    const ordered = settings.ordered === true;
    const updateItem = (idx: number, val: string) => {
      const next = [...items];
      next[idx] = val;
      onChange({ items: next });
    };
    const addItem = () => onChange({ items: [...items, 'Nowy item'] });
    const removeItem = (idx: number) => {
      const next = items.filter((_, i) => i !== idx);
      onChange({ items: next });
    };
    return (
      <div className="space-y-3" data-testid="list-properties-editor">
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={ordered}
            onChange={(e) => onChange({ ordered: e.target.checked })}
            className="h-4 w-4 rounded border-border-primary text-accent-600"
            data-testid="list-prop-ordered"
          />
          <span className="text-text-secondary">Lista numerowana</span>
        </label>
        <div className="space-y-1">
          <label className="block text-xs font-medium text-text-secondary">Elementy listy</label>
          {items.map((item, idx) => (
            <div key={idx} className="flex gap-1">
              <input
                type="text"
                value={item}
                onChange={(e) => updateItem(idx, e.target.value)}
                className="flex-1 rounded-md border border-border-primary bg-bg-primary px-2 py-1 text-sm"
                data-testid={`list-prop-item-${idx}`}
              />
              <button
                type="button"
                onClick={() => removeItem(idx)}
                className="rounded-md border border-border-primary px-2 py-1 text-xs text-text-secondary hover:bg-bg-tertiary"
                aria-label="Usuń element"
                data-testid={`list-prop-item-remove-${idx}`}
              >
                ✕
              </button>
            </div>
          ))}
          <button
            type="button"
            onClick={addItem}
            className="mt-1 w-full rounded-md border border-dashed border-border-primary px-2 py-1 text-xs text-text-secondary hover:border-accent-400 hover:text-accent-600"
            data-testid="list-prop-add"
          >
            + Dodaj element
          </button>
        </div>
      </div>
    );
  },
  render: ({ element, mode }) => {
    const settings = (element.settings ?? {}) as { items?: string[]; ordered?: boolean };
    const items = Array.isArray(settings.items)
      ? settings.items.filter((s) => typeof s === 'string')
      : [];
    const ordered = settings.ordered === true;

    if (items.length === 0) {
      if (mode === 'builder') {
        return (
          <div
            className="rounded border border-dashed border-border-secondary p-2 text-xs text-text-tertiary"
            data-testid={`list-element-${element.id}`}
          >
            (pusta lista — dodaj itemy w panelu właściwości)
          </div>
        );
      }
      return null;
    }

    const Tag = ordered ? 'ol' : 'ul';
    const listClass = ordered ? 'list-decimal' : 'list-disc';

    return (
      <Tag
        className={`${listClass} pl-5 text-sm text-text-secondary space-y-1`}
        data-testid={`list-element-${element.id}`}
        data-element-type="list"
      >
        {items.map((item, idx) => (
          <li key={idx}>{item || ' '}</li>
        ))}
      </Tag>
    );
  },
};
